<template lang="pug">
.v-field-code.field-code
	.field-code__list
		.field-code__el(v-if="codeData.lengthCode" )
			input.field-code__input(v-for="(item, index) in codeData.lengthCode"
					type="text"
					inputmode="numeric"
					pattern="[0-9]*"
					autocapitalize="off"
					autocorrect="off"
					spellcheck="false"
					:autocomplete="index === 0 ? 'one-time-code' : 'off'"
					:name="index === 0 ? 'one-time-code' : null"
					v-model="code[index]"
					:ref="setItemRef"
					@input="onInput(index, $event)"
					@paste.prevent="onPaste"
					:maxlength="index === 0 ? codeData.lengthCode : 1"
					@focus="focusStep = index"
					@blur="focusStep = 0"
					:class="{'error': codeData.error}"
					:disabled="codeData.blocked")
		.field-code__footer
			.field-code__timer(v-if="localTimer > 0") {{codeData.timerText}} {{localTimer}}
			button.field-code__action.link-blue.c-primary-shade-2(
					v-if="!localTimer"
					type="button"
					@click="requestCode") {{codeData.endTimerText}}
			.field-code__error.c-red-shade-2(v-if="codeData.error") {{codeData.error}}
			.field-code__message(v-if="codeData.blockedMessage" v-html="codeData.blockedMessage")
</template>

<script>
	// момент окончания отсчёта (timestamp, мс), id интервала и телефон, для
	// которого запущен отсчёт, хранятся вне компонента: v-field-code
	// пересоздаётся при ре-рендерах формы, и хранить это per-instance нельзя —
	// старые экземпляры тикали бы параллельно ("летит" счётчик). Держим ровно
	// один интервал на всё приложение
	let codeTimerEndTime = 0;
	let codeTimerIntervalId = null;
	let codeTimerPhone = null;

	export default  {
		props: {
			codeData: {
				type: Object,
				default: {

				},
				required: true
			},
			timer: {
				type: Number,
				required: true
			},
			modelValue: {
				type: [String, Number],
				default: ''
			}
		},
		emits: ['update:model-value', 'requestCode'],
		data() {
			return {
				itemRefs: [],
				code: Array.from({length: Number(this.codeData.lengthCode) || 4}, () => ''),
				localCodeString: '',
				focusStep: null,
				localTimer: this.timer,
			}
		},

		watch: {
			modelValue: {
				immediate: true,
				handler(value) {
					this.applyCode(value);
				}
			},
			'codeData.lengthCode'() {
				this.applyCode(this.modelValue);
			}
		},

		methods:{
			setItemRef(el) {
				if (el) {
					this.itemRefs.push(el)
				}
			},

			requestCode() {
				this.resetTimer();
				this.$emit('requestCode');
			},

			getCodeLength() {
				return Number(this.codeData.lengthCode) || 4;
			},

			applyCode(value, emit = false, focus = false) {
				const length = this.getCodeLength();
				const digits = String(value ?? '').replace(/\D/g, '').slice(0, length);
				this.code = Array.from({length}, (_, index) => digits[index] || '');
				this.localCodeString = digits;

				if (emit && digits.length === length) {
					this.$emit('update:model-value', digits);
				}
				if (focus && digits.length) {
					this.$nextTick(() => this.itemRefs[Math.min(digits.length, length) - 1]?.focus());
				}
			},

			onInput(index, event) {
				const rawValue = String(event?.target?.value ?? this.code[index] ?? '');
				const digits = rawValue.replace(/\D/g, '');
				if (digits.length > 1) {
					this.applyCode(digits, true, true);
					return;
				}

				this.code[index] = digits.slice(0, 1);
				if (event?.target) {
					event.target.value = this.code[index];
				}
				this.localCodeString = this.code.join('');
				if (this.localCodeString.length === this.getCodeLength()) {
					this.$emit('update:model-value', this.localCodeString);
					return;
				}
				if (this.code[index] !== '' && index < this.itemRefs.length - 1) {
					this.itemRefs[index + 1]?.focus();
				}
			},

			onPaste(event) {
				const value = event?.clipboardData?.getData('text') || '';
				this.applyCode(value, true, true);
			},
			// пересчёт остатка по абсолютному времени окончания
			syncTimer() {
				this.localTimer = Math.max(0, Math.ceil((codeTimerEndTime - Date.now()) / 1000));
			},

			startTimer() {
				if (!this.timer) return;
				const phone = this.codeData?.phone || '';
				// новый момент окончания задаём ТОЛЬКО когда сменился номер (код
				// пришёл на другой телефон) или прежний отсчёт истёк. При ошибке
				// ввода и прочих ре-рендерах (тот же номер) endTime сохраняется,
				// чтобы счётчик не сбрасывался
				const isNewCode = phone !== codeTimerPhone || !codeTimerEndTime || codeTimerEndTime <= Date.now();
				if (isNewCode) {
					codeTimerPhone = phone;
					codeTimerEndTime = Date.now() + this.timer * 1000;
				}
				// интервал всегда перепривязываем на текущий (актуальный)
				// экземпляр: при ре-рендере старый экземпляр размонтируется, и
				// если бы интервал остался на его syncTimer — он обновлял бы
				// невидимый localTimer, а на экране счётчик бы "застыл".
				// clearInterval гарантирует, что параллельных интервалов нет
				clearInterval(codeTimerIntervalId);
				codeTimerIntervalId = setInterval(this.syncTimer, 1000);
				this.syncTimer();
			},

			// принудительный перезапуск отсчёта — при нажатии "отправить код
			// повторно" (сервер шлёт новый код на тот же номер)
			resetTimer() {
				codeTimerEndTime = 0;
				this.startTimer();
			},

			handleKeyDown(e) {
				if (!this.itemRefs.includes(e.target)) return;
				let {key} = e;
				switch (key) {
					case 'ArrowRight' :
						if(this.focusStep < this.itemRefs?.length - 1) {
							this.focusStep++;
							this.itemRefs[this.focusStep].focus()
						}
					break;
					case 'ArrowLeft' :
						if(this.focusStep > 0) {
							this.focusStep--;
							this.itemRefs[this.focusStep].focus()
						}
					break;
					case 'Backspace' :
						if(this.focusStep > 0) {

							if (e.target.value !== '') {
								return;
							}

							this.focusStep--;
							this.itemRefs[this.focusStep].focus()
						}
						break;
				}


			}
		},
		beforeUpdate() {
			this.itemRefs = []
		},
		created() {
			document.addEventListener('keydown', this.handleKeyDown)
			this.$nextTick().then(() => this.startTimer());
		},
		beforeUnmount() {
			// модульный интервал НЕ трогаем — он принадлежит активному
			// экземпляру; при ре-рендере старый экземпляр размонтируется,
			// а новый перехватит интервал на себя в startTimer
			document.removeEventListener('keydown', this.handleKeyDown);
		},
	}
</script>
